import {hideLoading, resetIsManualReteachPage, showLoading, showSnackbar} from 'stores/actions/appStateAction';
import {hideModal, showModal} from 'stores/actions/modalStateAction';
import {showModalType} from 'stores/actions/typedModalAction';
import {
  CommonButtonLabel,
  FastRobotErrorcode,
  Feedback,
  ModalSize,
  ModalType,
  PlateType,
  TipCaddyType,
  TrayType,
} from '../../../constants/dataEnum';
import {fastRecovery} from '../recoveryBusiness';
import {generateRowColTipCount} from '../plateTypeEditorBusiness';
import {convertRowToAlphabet} from '../commonTaskListBusiness';
import {
  postManualReteachReturnTips,
  postManualReteachScanTips,
  recoveryEjectAndScanStuckTips,
} from '../instrumentBusiness';
import {resolveIntl, setErrorCodeNumber} from '../commonBusiness';
import {
  showCaddyErrorModal,
  showErrorMissingTrayModal,
  showFallenTipErrorModal,
  showModalMotorCollisionSkipped,
  showStuckTipModal,
  showTipsNotEnoughModal,
  showTrayNotDetectedModal,
} from '../modalStateBusinessAction';
import {showConfirmationModal} from '../commonModalBusiness';
import {safeScanTrayId} from '../trayBusiness';
import {fastCommonActuatorAPIErrorHandler} from '../errorHandlerBusiness';

const intl = resolveIntl();

export const manualReteachMissingTrayHandler = () => async (dispatch) => {
  dispatch(showLoading());
  await dispatch(recoveryEjectAndScanStuckTips());
  const scanTrayResponse = await dispatch(safeScanTrayId());
  dispatch(resetIsManualReteachPage());
  dispatch(hideLoading());

  if (scanTrayResponse !== FastRobotErrorcode.NoError) {
    dispatch(fastCommonActuatorAPIErrorHandler(scanTrayResponse));
  }
};

export function manualReteachErrorHandler(fastErrorCode, payload = {}) {
  const scanTips = (isIgnore) => async (dispatch, getState) => {
    const state = getState();
    const {sessionState, plateTypeEditorState, reteachPlateState, appState} = state;
    const {username} = sessionState.user;
    const {manualReteachPlate} = reteachPlateState;
    const {isManualReteachPage} = appState;
    const {plateType, id} = plateTypeEditorState;
    const {deckNumber, selectedWellStyle} = manualReteachPlate;
    const {row, col, rowTipCount, colTipCount, labwareIndex} = generateRowColTipCount(selectedWellStyle);
    const groupIndex = plateType === PlateType.IFC ? labwareIndex : 0;
    const alphabethRow = convertRowToAlphabet(row);
    dispatch(
      showLoading(
        intl.formatMessage({id: 'plateTypeEditorContainer.manualReteachPlate.modalPreparation.Confirm.loading'}),
      ),
    );
    dispatch(
      postManualReteachScanTips(
        username,
        id,
        deckNumber,
        colTipCount,
        rowTipCount,
        alphabethRow,
        col,
        isIgnore ? true : isManualReteachPage,
        groupIndex,
      ),
    );
  };

  return async (dispatch, getState) => {
    const state = getState();
    const {sessionState} = state;
    const {username} = sessionState.user;
    const resumeCallback = () => {
      dispatch(scanTips(false));
    };

    const abortCallBack = () => {
      dispatch(
        showLoading(
          intl.formatMessage({
            id: 'plateTypeEditorContainer.manualReteachPlate.returnTipsConfirmation.content.loading',
          }),
        ),
      );
      dispatch(postManualReteachReturnTips(username, false));
    };

    const ignoreCallback = () => {
      dispatch(scanTips(true));
    };

    await dispatch(setErrorCodeNumber(fastErrorCode));
    switch (fastErrorCode) {
      case FastRobotErrorcode.NotReady:
      case FastRobotErrorcode.ActuatorNotReady:
      case FastRobotErrorcode.ActuatorAborted:
        dispatch(
          showModalMotorCollisionSkipped(
            async () => {
              dispatch(showLoading(intl.formatMessage({id: 'recoveryAction.loading'})));
              await dispatch(
                fastRecovery(() => {
                  dispatch(
                    showConfirmationModal(
                      'plateTypeEditorContainer.modal.manualReteachPlate',
                      resumeCallback,
                      abortCallBack,
                    ),
                  );
                }),
              );
            },
            async () => {
              dispatch(showLoading(intl.formatMessage({id: 'recoveryAction.loading'})));
              await dispatch(fastRecovery());
              dispatch(hideLoading());
            },
          ),
        );
        break;

      case FastRobotErrorcode.CADDY_NOT_FOUND:
        dispatch(showCaddyErrorModal({errorCode: fastErrorCode}, resumeCallback, abortCallBack));
        break;

      case FastRobotErrorcode.UNSUPPORTED_LABWARE:
        dispatch(
          showModal(
            intl.formatMessage({id: 'plateTypeEditorContainer.modal.manualReteachPlate.notSupportLabware.title'}),
            intl.formatMessage({id: 'plateTypeEditorContainer.modal.manualReteachPlate.notSupportLabware.message'}),
            [
              {
                style: 'primary',
                handlerClick: () => {
                  dispatch(hideModal());
                },
                text: intl.formatMessage({id: CommonButtonLabel.CLOSE}),
              },
            ],
            () => {},
            '',
            false,
            ModalSize.MEDIUM,
          ),
        );
        break;

      case FastRobotErrorcode.TipsNotEnough:
        dispatch(showTipsNotEnoughModal({errorCode: fastErrorCode}, resumeCallback, abortCallBack));
        break;

      case FastRobotErrorcode.FALLEN_TIPS_DETECTED:
        dispatch(showFallenTipErrorModal({errorCode: fastErrorCode}, resumeCallback, abortCallBack, ignoreCallback));
        break;

      case FastRobotErrorcode.STUCK_TIPS_DETECTED:
        dispatch(showStuckTipModal(() => dispatch(fastRecovery(abortCallBack))));
        break;

      case FastRobotErrorcode.NO_TRAY_ID_FOUND:
        dispatch(showTrayNotDetectedModal(() => dispatch(fastRecovery())));
        break;

      case FastRobotErrorcode.MissingTray:
        dispatch(showErrorMissingTrayModal(() => dispatch(manualReteachMissingTrayHandler())));
        break;

      case FastRobotErrorcode.CADDY_TYPE_MISMATCHED: {
        const {trayType, mismatchedNestIds = []} = payload;

        dispatch(
          showModalType(ModalType.CADDY_DETECTION, {
            trayType,
            firstNestLocation: mismatchedNestIds[0],
            secondNestLocation: mismatchedNestIds[1],
            expectedCaddy:
              trayType === TrayType.HIGH_VOLUME_CADDY ? TipCaddyType.HIGH_VOLUME_CADDY : TipCaddyType.LOW_VOLUME_CADDY,
          }),
        );
        break;
      }
      case FastRobotErrorcode.ActuatorIllegalPos: {
        dispatch(
          showSnackbar(
            Feedback.WARNING,
            intl.formatMessage({
              id: 'trayCalibrationContainer.notification.reachedMaximumLimit',
            }),
          ),
        );
        break;
      }
      default:
        break;
    }
  };
}
